import React, { useState, useEffect } from 'react';
import firebase from 'firebase/app';
import { Typography } from 'antd';

import { useCurrentUser } from '../../models/useCurrentUser';
import { useUser } from '../../models/useUser';
import { isMobileWeb } from '../../utils/authFlow';

const { Text } = Typography;

const DISMISSED_FIELD = 'fasterSigninPromptDismissedAt';

/** Which sign-in methods the auth user carries, normalised. */
export function providersOf(user) {
  if (!user) return [];
  return (user.providerData || [])
    .map((p) => p && p.providerId)
    .map((id) => {
      if (id === 'google.com') return 'google';
      if (id === 'apple.com') return 'apple';
      if (id === 'password') return 'email';
      if (id === 'phone') return 'phone';
      return null;
    })
    .filter(Boolean);
}

/**
 * Show the prompt only to a phone-only account that has not already said no.
 * Every phone sign-in costs an SMS; a linked Google or Apple makes the next
 * ones free. Once, dismissible, then only on the Security page.
 */
export function shouldOfferFasterSignin(authUser, firestoreUser) {
  if (!authUser || authUser.isAnonymous) return false;
  if (firestoreUser && firestoreUser[DISMISSED_FIELD]) return false;
  const providers = providersOf(authUser);
  return providers.includes('phone') && !providers.includes('google') && !providers.includes('apple');
}

const button = {
  width: '100%', height: 48, fontSize: 16, fontWeight: 600, borderRadius: 8,
  cursor: 'pointer', marginBottom: 8,
};

const providerLabel = (which) => (which === 'google' ? 'Google' : 'Apple');

function makeProvider(which) {
  const provider = which === 'google'
    ? new firebase.auth.GoogleAuthProvider()
    : new firebase.auth.OAuthProvider('apple.com');
  provider.addScope('email');
  if (which === 'apple') provider.addScope('name');
  return provider;
}

const POPUP_DEADLINE_MS = 60000;

/**
 * Link the provider to the signed-in user and mirror the provider list.
 *
 * Phones use the redirect flow (iOS Safari blocks or orphans popups; in-app
 * webviews reject Google OAuth — the same rule the login buttons follow in
 * authFlow.js). The page unloads and comes back signed in with the provider
 * attached, so the card simply no longer offers. Desktop keeps the popup, with a
 * deadline so an orphaned window can never leave the card stuck on "Opening…".
 */
async function linkProvider(which, uid) {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Not signed in');
  const provider = makeProvider(which);
  if (isMobileWeb() && typeof user.linkWithRedirect === 'function') {
    await user.linkWithRedirect(provider);
    return 'redirect';
  }
  await Promise.race([
    user.linkWithPopup(provider),
    new Promise((_, reject) => setTimeout(() => reject({ code: 'auth/popup-closed-by-user' }), POPUP_DEADLINE_MS)),
  ]);
  try { await user.reload(); } catch (_) { /* noop */ }
  const providers = Array.from(new Set(providersOf(firebase.auth().currentUser)));
  await firebase.firestore().collection('users').doc(uid).set(
    { authProviders: providers, updatedAt: firebase.firestore.FieldValue.serverTimestamp() },
    { merge: true },
  );
  return 'linked';
}

/** What to tell the person when a link fails; null when they just closed the popup. */
export function linkErrorMessage(err, which) {
  const code = err && err.code;
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return null;
  if (code === 'auth/credential-already-in-use' || code === 'auth/account-exists-with-different-credential') {
    return `That ${providerLabel(which)} account already belongs to another Striketip account. Try a different one, or add it later from Security.`;
  }
  return (err && err.message) || 'Could not add that sign-in. Try again later from Security.';
}

function ProviderButtons({ busy, onPick }) {
  return (
    <>
      <button
        type="button"
        onClick={() => onPick('google')}
        disabled={!!busy}
        style={{ ...button, background: '#fff', border: '1px solid #d9d9d9', color: '#262626' }}
      >
        {busy === 'google' ? 'Opening Google…' : 'Add Google'}
      </button>
      <button
        type="button"
        onClick={() => onPick('apple')}
        disabled={!!busy}
        style={{ ...button, background: '#000', border: '1px solid #000', color: '#fff' }}
      >
        {busy === 'apple' ? 'Opening Apple…' : 'Add Apple'}
      </button>
    </>
  );
}

/**
 * One card, one tap: link Google or Apple to the account the person just made,
 * so their next sign-in does not need a text message. Shown on the team page
 * right after a join and once on the dashboard; "Not now" hides it for good.
 */
export default function FasterSigninCard({ onLinked }) {
  const [, authUser] = useCurrentUser();
  const uid = authUser && !authUser.isAnonymous ? authUser.uid : null;
  const [userLoading, firestoreUser] = useUser(uid);
  const [busy, setBusy] = useState(null); // 'google' | 'apple' | null
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => { setHidden(false); }, [uid]);

  // Once linked the account is no longer phone-only, so the offer gate would hide
  // the "done" line too — check done first.
  if (hidden) return null;
  // Wait for the users doc before deciding: a dismissed card must not flash in.
  if (!done && (userLoading || !shouldOfferFasterSignin(authUser, firestoreUser))) return null;

  const markDismissed = async () => {
    setHidden(true);
    try {
      await firebase.firestore().collection('users').doc(uid).set(
        { [DISMISSED_FIELD]: firebase.firestore.FieldValue.serverTimestamp() },
        { merge: true },
      );
    } catch (err) {
      console.warn('[faster-signin] dismiss write failed:', err && err.message);
    }
  };

  const link = async (which) => {
    setError(null);
    setBusy(which);
    try {
      const outcome = await linkProvider(which, uid);
      if (outcome === 'redirect') return; // the page is leaving; nothing more to draw
      setDone(true);
      if (onLinked) onLinked(which);
    } catch (err) {
      setError(linkErrorMessage(err, which));
    } finally {
      setBusy(null);
    }
  };

  if (done) {
    return (
      <div className="faster-signin-card" style={{ borderRadius: 12, padding: 16, background: '#f6ffed', border: '1px solid #b7eb8f', marginBottom: 16 }}>
        <Text strong style={{ fontSize: 16, display: 'block' }}>Done. Next time, just tap the Google or Apple button to log in.</Text>
      </div>
    );
  }

  return (
    <div className="faster-signin-card" style={{ borderRadius: 12, padding: 16, background: '#fafafa', border: '1px solid #d9d9d9', marginBottom: 16 }}>
      <Text strong style={{ fontSize: 18, display: 'block', marginBottom: 4 }}>Add more ways to log in</Text>
      <Text style={{ fontSize: 16, color: '#595959', display: 'block', marginBottom: 12 }}>
        Add Google or Apple and you won’t need a text message to log in next time.
      </Text>
      <ProviderButtons busy={busy} onPick={link} />
      {error && (
        <Text type="danger" style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>{error}</Text>
      )}
      <button
        type="button"
        onClick={markDismissed}
        style={{ background: 'none', border: 'none', color: '#8c8c8c', fontSize: 16, minHeight: 44, width: '100%', cursor: 'pointer' }}
      >
        Not now
      </button>
    </div>
  );
}
